<!-- SplitProvider.vue -->
<template>
  <slot />
</template>

<script>

import {defineComponent, ref, provide, onMounted, onUnmounted, watch, inject } from "vue";
import {
  SPLIT_CACHE_PREFIX,
  createSplitFetchQueue,
  fetchCachedClientTreatments,
  fetchClientTreatments,
  formatSplitsForGA4,
} from './utils.js';
import { useStore } from "vuex";

const isSSR = typeof window === 'undefined';

// Lazy-load Split SDK to keep it out of the main bundle
let _splitModulePromise = null;
function loadSplitModule() {
  if (!_splitModulePromise) {
    _splitModulePromise = import(/* webpackChunkName: "splitio-sdk" */ '@splitsoftware/splitio-browserjs');
  }
  return _splitModulePromise;
}

export default defineComponent({
  name: "SplitProvider",
  props: {
    apiKey: { type: String, required: true },
    userKey: { type: String, required: true },
    readyTimeout: { type: Number, default: 1.5 },
  },
  setup(props) {
    const $eventBus = inject('$eventBus');
    const store = useStore();
    const splits = store.getters['glb/getSplits'];

    const splitClient = ref(null);
    const splitIsReady = ref(false);
    const currentUserKey = ref(props.userKey);
    const splitFetchQueue = createSplitFetchQueue({
      timeoutMs: (props.readyTimeout + 5) * 1000,
    });

    const markSplitReady = () => {
      splitIsReady.value = true;
      splitFetchQueue.setReady(true);
    };

    const markSplitNotReady = () => {
      splitIsReady.value = false;
      splitFetchQueue.resetReady();
    };

    const createSplitFactory = async (userKey) => {
      const { SplitFactory, InLocalStorage } = await loadSplitModule();
      return SplitFactory({
        core: {
          authorizationKey: props.apiKey,
          key: userKey,
        },
        startup: {
          readyTimeout: props.readyTimeout,
        },
        impressionListener: {
          logImpression: ({ impression: { feature, treatment } }) => {
            const eventLabel = `${feature}:${treatment}`;
            if(treatment !== "control" && treatment !== "default") {
              $eventBus.$emit(
                  'ga4',
                  { en: 'split_impression', el: eventLabel },
                  {pageData: { split_treatments: formatSplitsForGA4(splits, fetchCachedClientTreatments())}}
              );
            }
          },
        },
        storage: InLocalStorage({ prefix: SPLIT_CACHE_PREFIX }),
        sync: {
          splitFilters: [{ type: 'bySet', values: ['movoto_cw_frontend'] }],
        },
      });
    };

    const setupEventListeners = (client) => {
      const events = {
        [client.Event.SDK_READY]: markSplitReady,
        [client.Event.SDK_READY_FROM_CACHE]: markSplitReady,
        [client.Event.SDK_READY_TIMED_OUT]: () => {
          console.error("Split SDK Ready Timed Out");
          markSplitReady();
        },
        [client.Event.SDK_UPDATE]: () => console.error("Split SDK Updated"),
      };

      Object.entries(events).forEach(([event, handler]) => client.on(event, handler));
    };

    const initializeSplit = async (userKey) => {

      if (isSSR) return;

      const splitFactory = await createSplitFactory(userKey);
      splitClient.value = splitFactory.client();

      setupEventListeners(splitClient.value);
    };

    // Recreate split whenever user key is changed
    const setUserKey = (newUserKey) => {
      if (newUserKey !== currentUserKey.value) {
        if (splitClient.value) {
          markSplitNotReady();
          splitClient.value.destroy(); // Clean up the old client
        }
        currentUserKey.value = newUserKey;
        initializeSplit(newUserKey); // Initialize a new client with the new user key
      }
    };

    const executeFetchTreatments = (featureFlags, options = {}) => {
      return fetchClientTreatments({
        client: splitClient.value,
        isReady: splitIsReady.value,
        userKey: currentUserKey.value,
        featureFlags,
        options,
      });
    };

    const fetchTreatments = (featureFlags, options = {}) => {
      return splitFetchQueue.enqueue(() => executeFetchTreatments(featureFlags, options));
    };

    const fetchServerTreatments = (featureFlags = [], withConfig = false) => {
      return featureFlags.map((featureFlag) => {
        const treatment = (splits && splits[featureFlag]) || 'control';
        const config = withConfig ? splits[`${featureFlag}_config`] || {} : undefined;
        return {
          [featureFlag]: withConfig ? { treatment, config } : treatment,
        };
      }).reduce((acc, curr) => ({ ...acc, ...curr }), {});
    };

    onUnmounted(() => splitClient.value?.destroy()); // Clean up SDK on component unmount

    watch(() => props.userKey, setUserKey);

    onMounted(() => {
      if (isSSR) return;
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => initializeSplit(props.userKey), { timeout: 3000 });
      } else {
        setTimeout(() => initializeSplit(props.userKey), 2000);
      }
    });

    provide("split", {
      client: splitClient,
      key: currentUserKey,
      ready: splitIsReady,
      fetchTreatments,
      fetchServerTreatments,
      fetchCachedClientTreatments
    });
  },
});
</script>